import { useEffect, useRef, useState } from "react";
import { LegalComplianceActionBar } from "@/features/legal/components/LegalComplianceStep/LegalComplianceActionBar";
import { LegalComplianceDocumentCard } from "@/features/legal/components/LegalComplianceStep/LegalComplianceDocumentCard";
import { LegalComplianceHeader } from "@/features/legal/components/LegalComplianceStep/LegalComplianceHeader";
import { LegalComplianceLoadError } from "@/features/legal/components/LegalComplianceStep/LegalComplianceLoadError";
import { LegalCompliancePrivacyNote } from "@/features/legal/components/LegalComplianceStep/LegalCompliancePrivacyNote";
import { LegalComplianceSkeleton } from "@/features/legal/components/LegalComplianceStep/LegalComplianceSkeleton";
import { LegalComplianceTermsModal } from "@/features/legal/components/LegalComplianceStep/LegalComplianceTermsModal";
import { LegalDocumentModal } from "@/features/legal/components/LegalDocumentModal";
import { LEGAL_COMPLIANCE_COPY } from "@/features/legal/config/legal.config";
import { useLegalCompliance } from "@/features/legal/hooks/useLegalCompliance";
import type { LegalCompliance, LegalDocument } from "@/features/legal/types/legalCompliance.type";

type LegalComplianceStepProps = {
  onAccepted: (legalCompliance: LegalCompliance) => void
  onNothingToAccept: () => void
  onRefused: () => void
  onLogout: () => void
};

const resolveNotice = (isOutdated: boolean, isPresentationOutdated: boolean, hasAcceptError: boolean): string | null => {
  if (isPresentationOutdated) return LEGAL_COMPLIANCE_COPY.PRESENTATION_OUTDATED_NOTICE;
  if (isOutdated) return LEGAL_COMPLIANCE_COPY.OUTDATED_NOTICE;
  if (hasAcceptError) return LEGAL_COMPLIANCE_COPY.ACCEPT_ERROR;
  return null;
};

// Onboarding step shown after the other login steps when the employee has
// not accepted the current legal compliance sections yet. Hands over through
// `onNothingToAccept` when the server reports nothing left to accept.
export const LegalComplianceStep = ({
  onAccepted, onNothingToAccept, onRefused, onLogout,
}: LegalComplianceStepProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const {
    legalCompliance, isLoading, isLoadError, reload, accept, isAccepting, isOutdated, isPresentationOutdated, hasAcceptError,
  } = useLegalCompliance();
  // Ticking is tied to the version displayed: a new version (re-read after
  // a 409) starts unchecked again.
  const [checkedVersion, setCheckedVersion] = useState<string | null>(null);
  const [openDocument, setOpenDocument] = useState<LegalDocument | null>(null);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  useEffect(() => {
    // A load failure also leaves `legalCompliance` null: it must show the
    // error state, not be mistaken for "nothing left to accept".
    if (!isLoading && !isLoadError && legalCompliance === null) {
      onNothingToAccept();
    }
  }, [isLoading, isLoadError, legalCompliance, onNothingToAccept]);

  useEffect(() => {
    if (legalCompliance) {
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [legalCompliance]);

  if (isLoading) return <LegalComplianceSkeleton />;
  if (isLoadError) return <LegalComplianceLoadError onRetry={reload} onLogout={onLogout} />;
  if (!legalCompliance) return null;

  const isChecked = checkedVersion === legalCompliance.version;

  const openDocumentFromTerms = (document: LegalDocument) => {
    setIsTermsOpen(false);
    setOpenDocument(document);
  };

  return (
    <div className="space-y-8">
      <LegalComplianceHeader headingRef={headingRef} legalCompliance={legalCompliance} />
      <LegalComplianceDocumentCard legalCompliance={legalCompliance} onRead={() => setIsTermsOpen(true)} />
      <LegalComplianceActionBar
        actionLabel={legalCompliance.action_label}
        canAccept={legalCompliance.can_accept}
        isChecked={isChecked}
        onCheckedChange={(checked) => setCheckedVersion(checked ? legalCompliance.version : null)}
        notice={resolveNotice(isOutdated, isPresentationOutdated, hasAcceptError)}
        isPresentationOutdated={isPresentationOutdated}
        isAccepting={isAccepting}
        onAccept={() => accept(legalCompliance, { onSuccess: () => onAccepted(legalCompliance) })}
        onRefuse={legalCompliance.can_accept ? onRefused : onLogout}
      />
      <LegalCompliancePrivacyNote documents={legalCompliance.documents} onOpen={setOpenDocument} />
      <LegalComplianceTermsModal
        legalCompliance={legalCompliance}
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
        onOpenDocument={openDocumentFromTerms}
      />
      <LegalDocumentModal document={openDocument} onClose={() => setOpenDocument(null)} />
    </div>
  );
};
